import { useState, useEffect } from 'react';
import api from '../../api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, Trash2 } from 'lucide-react';

export const DeleteVirtDialog = ({ open, onOpenChange, previewUrl, deleteUrl, onDeleted }) => {
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open || !previewUrl) {
      setPreview(null);
      setConfirmText('');
      return;
    }
    let cancelled = false;
    setLoadingPreview(true);
    api.get(previewUrl)
      .then(res => { if (!cancelled) setPreview(res.data); })
      .catch(() => {
        if (!cancelled) {
          toast.error('No se pudo calcular el impacto del borrado');
          onOpenChange(false);
        }
      })
      .finally(() => { if (!cancelled) setLoadingPreview(false); });
    return () => { cancelled = true; };
  }, [open, previewUrl]);

  const nameMatches = preview && confirmText.trim() === preview.name;

  const handleDelete = async () => {
    if (!nameMatches) return;
    setDeleting(true);
    try {
      const res = await api.delete(deleteUrl);
      toast.success(res.data?.message || 'Eliminado correctamente');
      onOpenChange(false);
      onDeleted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!deleting) onOpenChange(v); }}>
      <DialogContent data-testid="delete-virt-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" /> Eliminar definitivamente
          </DialogTitle>
          <DialogDescription>Esta acción es permanente y no se puede deshacer.</DialogDescription>
        </DialogHeader>

        {loadingPreview || !preview ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Calculando impacto...
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm">
              Vas a eliminar <span className="font-medium">{preview.type}</span>{' '}
              <span className="font-mono font-semibold text-foreground">{preview.name}</span> y todos sus datos descubiertos:
            </p>
            <ul className="rounded-lg border border-border bg-muted/30 divide-y divide-border text-sm">
              {preview.items.map((it) => (
                <li key={it.label} className="flex items-center justify-between px-3 py-2">
                  <span className="text-muted-foreground">{it.label}</span>
                  <span className="font-mono font-semibold" data-testid={`delete-count-${it.label}`}>{it.count}</span>
                </li>
              ))}
            </ul>
            {preview.extra_note && (
              <p className="text-xs text-amber-600 flex items-start gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />{preview.extra_note}
              </p>
            )}
            <div className="rounded-md bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
              Nota: si el colector externo sigue escaneando este elemento, puede reaparecer en el próximo scan. Para que no vuelva, dejá de escanearlo desde el colector.
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">
                Para confirmar, escribí <span className="font-mono font-semibold text-foreground">{preview.name}</span>:
              </label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={preview.name}
                autoFocus
                data-testid="delete-confirm-input"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={deleting} data-testid="delete-cancel-btn">
            Cancelar
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={!nameMatches || deleting} data-testid="delete-confirm-btn">
            {deleting
              ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Eliminando...</>
              : <><Trash2 className="h-4 w-4 mr-1.5" /> Eliminar definitivamente</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
